import {
    Children,
    type FC,
    Fragment,
    type FragmentProps,
    isValidElement,
    type ReactElement,
    type ReactNode,
} from "react";
import type { RouteProps } from "react-router/dist/lib/components";
import {
    Route,
    type RouteObject,
    useRoutes,
} from "react-router-dom";
import { PermissionRoute } from "./PermissionRoute";
import { TitleRoute } from "./TitleRoute";

/**
 * Type-guard for react element being a fragment
 * @param element
 */
function isFragment(element: ReactElement): element is ReactElement<FragmentProps> {
    return element.type === Fragment;
}

/**
 * Type-guard for react element being a react-router route
 * @param element
 */
function isRoute(element: ReactElement): element is ReactElement<RouteProps> {
    return element.type === Route || element.type === PermissionRoute || element.type === TitleRoute;
}

/**
 * Create a route config from Rect children, essentially a clone of
 * remix-run/react-router/packages/react-router/lib/components.tsx -> createRoutesFromChildren
 * however it adds support for our custom routes
 * @param children
 */
const createRoutesFromShopfrontChildren = (children: ReactNode): Array<RouteObject> => {
    const routes: Array<RouteObject> = [];

    Children.forEach(children, element => {
        if(!isValidElement(element)) {
            return;
        }

        if(isFragment(element)) {
            routes.push(...createRoutesFromShopfrontChildren(element.props.children));

            return;
        }

        if(!(isRoute(element))) {
            throw new Error(
                `[${typeof element.type === "string" ? element.type : element.type.name}] is not a supported ShopfrontRouter component`
            );
        }

        const route: RouteObject = {
            caseSensitive: element.props.caseSensitive,
            element      : (element.type === PermissionRoute || element.type === TitleRoute) ?
                element :
                element.props.element,
            index: element.props.index,
            path : element.props.path,
        };

        if(element.props.children) {
            route.children = createRoutesFromShopfrontChildren(element.props.children);
        }

        routes.push(route);
    });

    return routes;
};

/**
 * Create a Shopfront router which accepts custom Route elements
 * @param children
 * @constructor
 */
export const ShopfrontRoutes: FC<{ children: ReactNode; }> = ({ children }) => {
    return useRoutes(createRoutesFromShopfrontChildren(children));
};
